import type {
  SegmentAudio,
  SegmentCollection,
  SegmentSubtitleGroup,
  SegmentText,
} from '@ve-pro/types/track.type'
import { useHistoryStore, useProjectStore, useTrackStore } from '@ve-pro/store'
import { SegmentType } from '@ve-pro/enums/segment'
import { ProjectMode } from '@ve-pro/enums/project'
import { TrackType } from '@ve-pro/enums/track-type'
import { setActiveSegment } from '../toggle-active-segment'
import {
  getConfigsSpeaker,
  updateSpeakerVoice,
} from '@ve-pro/store/project/config'
import { quitFragmentEdit } from '@ve-pro/store/tracks/segment/segment-fragment'
import _ from 'lodash'
import { SourceSegmentIdBizType } from '@ve-pro/enums/segment'
import { Gender } from '@com/components/speaker-gender/utils'
import { removeSegmentSync } from '../tracks-actions'

export function isSegmentSubtitleGroup(
  segment: SegmentCollection,
): segment is SegmentSubtitleGroup {
  return segment.type === SegmentType.SubtitleGroup
}

export function isSegmentSubtitle(
  segment: SegmentCollection,
): segment is SegmentText {
  return (
    segment.type === SegmentType.Text && segment.stage === ProjectMode.Subtitle
  )
}

/**
 * 是否是手动添加的字幕
 * @param segment {  SegmentCollection }
 * @returns { boolean }
 */
export function isManualAddingSegmentSubtitle(
  segment: SegmentCollection,
): segment is SegmentText {
  return isSegmentSubtitle(segment) && !segment.biz_type
}

export function isSegmentDubbing(
  segment: SegmentCollection,
): segment is SegmentAudio {
  return (
    segment.type === SegmentType.Audio && segment.stage === ProjectMode.Subtitle
  )
}

/**
 * 配音找字幕
 * @param segmentAudio 配音元件
 * @returns 字幕元件
 */
export function findSubtitle(
  segmentAudio: SegmentAudio,
): SegmentText | undefined {
  const trackStore = useTrackStore()

  return trackStore.segments.find(
    (segment): segment is SegmentText =>
      segmentAudio.source_segment_id > 0 &&
      segment.id === segmentAudio.source_segment_id &&
      isSegmentSubtitle(segment),
  )
}

/**
 * 字幕找配音
 * @param segmentText 字幕元件
 * @returns 配音元件
 */
export function findDubbing(
  segmentText: SegmentText,
): SegmentAudio | undefined {
  const trackStore = useTrackStore()

  return trackStore.segments.filter(
    (segment): segment is SegmentAudio =>
      segment.source_segment_id > 0 &&
      segment.source_segment_id === segmentText.id &&
      isSegmentDubbing(segment),
  )[0]
}

/**
 * 获取全部字幕元件 包含多语言字幕
 */
export function getAllLanguagesSubtitleSegment(): SegmentText[] {
  const trackStore = useTrackStore()
  const temp = [...trackStore.subtitleTracks].reverse()
  return (
    temp
      .reduce<SegmentText[]>((segments, track) => {
        if (track.type === TrackType.Text) {
          segments.push(
            ...track.segments.filter(
              (s): s is SegmentText => s.type === SegmentType.Text,
            ),
          )
        }
        return segments
      }, [])
      .sort((a, b) => a.start_time - b.start_time) || []
  )
}

/**
 * 获取全部原语言字幕元件
 */
export function getSubtitleSegment(): SegmentText[] {
  const trackStore = useTrackStore()
  const temp = [...trackStore.subtitleTracks].reverse()
  return (
    temp
      .reduce<SegmentText[]>((segments, track) => {
        if (track.type === TrackType.Text) {
          segments = segments.filter((s) => s.lang === '')
          segments.push(
            ...track.segments.filter(
              (s): s is SegmentText =>
                s.type === SegmentType.Text && s.lang === '',
            ),
          )
        }
        return segments
      }, [])
      .sort((a, b) => a.start_time - b.start_time) || []
  )
}

export async function enterSubtitleMode(): Promise<any> {
  const trackStore = useTrackStore()
  const historyStore = useHistoryStore()
  const projectStore = useProjectStore()

  // 进入字幕模式，要强制退出子元件的编辑
  trackStore.activeSegmentGroup = null

  // 进入字幕模式，要强制退出片段的编辑
  if (trackStore.activeSegmentFragment) {
    await quitFragmentEdit({
      pushHistory: false,
    })
  }

  setActiveSegment(null)

  projectStore.mode = ProjectMode.Subtitle

  historyStore.push()
}

/**
 * 仅删除改字幕的配音
 * @param subtitle { SegmentText }
 */
export function removeSubtitleDubbing(subtitle: SegmentText): void {
  const dub = findDubbing(subtitle)

  if (dub) {
    removeSegmentSync(dub)
  }
}

/**
 * 新增自定义speaker
 */

export function addCustomSpeaker(segmentVideoId = 0): string {
  const projectStore = useProjectStore()
  const configsSpeaker = projectStore.isReplaceProject
    ? { segmentVideoId: projectStore.configs.speaker[segmentVideoId] ?? {} }
    : undefined

  const speakers = _.uniq(getConfigsSpeaker(configsSpeaker)).sort()
  let n = speakers.length
  if (speakers.length) {
    const label = speakers[speakers.length - 1]!.split('_')
    n = Number(label[1]) + 1
  }
  const count = n < 10 ? `0${n}` : n
  const model = `SPEAKER_${count}`
  updateSpeakerVoice({
    segmentVideoId,
    speaker: model,
    lang: '',
    selectedGender: Gender.Neutral,
    clone: false,
  })

  return model
}

/**
 * 是否是通过视频识别出来的字幕
 */
export function isVideoRecognizedSubtitle(segment: SegmentCollection): boolean {
  return (
    isSegmentSubtitle(segment) &&
    segment.source_segment_id > 0 &&
    (!segment.biz_type ||
      segment.biz_type === SourceSegmentIdBizType.Recognized)
  )
}

/**
 * 是合并音频识别出来的元件
 */
export function isCombineSegment(segment: SegmentCollection): boolean {
  return Boolean(segment.source?.startsWith('combine-'))
}
